import React, { useState } from 'react'

import App from '../components/app/app'
import Contact from '../components/contact/contact'
import Filters from '../components/filters/filters'
import Header from '../components/header/header'
import { Title2 } from '../components/title/title'
import Wine from '../components/wine/wine'
import { filter } from '../domains/filters'
import { graphql } from 'gatsby'
import navigation from '../navigation/vinos'
import seo from '../seo/vinos'
import styles from './vinos.module.css'

export const query = graphql`
  query Wines {
    allMarkdownRemark {
      totalCount
      edges {
        node {
          frontmatter {
            path
            bodega
            nombre
            uva
            color
            precio
            stock
            agotado
            id
            fecha(formatString: "DD MMMM, YYYY")
            imagen {
              childImageSharp {
                fixed(width: 70) {
                  ...GatsbyImageSharpFixed
                }
              }
            }
          }
        }
      }
    }
    authorImage: file(relativePath: { eq: "author.jpg" }) {
      childImageSharp {
        fixed(width: 200, height: 183) {
          ...GatsbyImageSharpFixed
        }
      }
    }
  }
`

const getOptionsByType = (type, wines) => {
  const types = []

  if (wines && wines.length > 0) {
    wines.forEach(({ node }) => {
      const wine = node?.frontmatter

      if (wine[type] && !types.includes(wine[type])) {
        types.push(wine[type])
      }
    })
  }

  return types
}

const getPriceOptions = () => {
  const priceOptions = []

  for (let index = 0; index < filter?.options?.precio?.length; index++) {
    const priceOption = filter?.options?.precio[index]
    priceOptions.push(priceOption.label)
  }

  return priceOptions
}

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

const adaptFilter = (type, placeholder, options) => {
  const optionsList = []

  if (options && options.length > 0) {
    options.forEach((option) => {
      optionsList.push({
        value: option,
        label: capitalizeFirstLetter(option),
      })
    })
  }

  return {
    placeholder,
    type,
    options: optionsList,
  }
}

const getFilters = (wines) => {
  const colorOptions = getOptionsByType(filter.type.color, wines)
  const cellarOptions = getOptionsByType(filter.type.bodega, wines)
  const grapeOptions = getOptionsByType(filter.type.uva, wines)
  const priceOptions = getPriceOptions()

  const colorFilters = adaptFilter(
    filter.type.color,
    filter.placeholder.color,
    colorOptions,
  )

  const cellarFilters = adaptFilter(
    filter.type.bodega,
    filter.placeholder.bodega,
    cellarOptions,
  )

  const grapeFilters = adaptFilter(
    filter.type.uva,
    filter.placeholder.uva,
    grapeOptions,
  )

  const priceFilters = adaptFilter(
    filter.type.precio,
    filter.placeholder.precio,
    priceOptions,
  )

  return {
    [filter.type.color]: colorFilters,
    [filter.type.bodega]: cellarFilters,
    [filter.type.uva]: grapeFilters,
    [filter.type.precio]: priceFilters,
  }
}

const parsePriceToFloatNumber = (price) => {
  const priceReplacedDot = price.replace(',', '.')
  const priceRemovedEuro = priceReplacedDot.replace('€', '')
  return parseFloat(priceRemovedEuro)
}

const getMaxAndMinByPriceOption = (priceOption) => {
  for (let index = 0; index < filter?.options?.precio.length; index++) {
    const option = filter?.options?.precio[index]

    if (option.label === priceOption.label) {
      return {
        max: option?.value?.max || null,
        min: option?.value?.min || null,
      }
    }
  }
}

const Wines = (props) => {
  const winesNumber = props?.data?.allMarkdownRemark?.totalCount
  const WINES_TITLE = `Catálogo de nuestros ${winesNumber} vinos`
  const wines = props?.data?.allMarkdownRemark?.edges
  const filters = getFilters(wines)

  const contact = {
    image: props?.data?.authorImage?.childImageSharp?.fixed,
  }

  const getDefaultFilterStatus = (type) => ({
    type,
    options: [],
    filtered: [],
  })

  const [winesFiltered, setWinesFiltered] = useState(wines)
  const [filtersStatus, setFiltersStatus] = useState([
    getDefaultFilterStatus(filter.type.bodega),
    getDefaultFilterStatus(filter.type.color),
    getDefaultFilterStatus(filter.type.uva),
    getDefaultFilterStatus(filter.type.precio),
  ])

  const getLongestCollection = (a, b) => {
    if (a.length > b.length) {
      return [...a]
    } else if (a.length === b.length) {
      return -1
    }

    return [...b]
  }

  const getWinePositionInWines = (name, wines) => {
    return wines.findIndex((wine) => wine.node.frontmatter.nombre === name)
  }

  const getWinesJoined = (winesA, winesB) => {
    return [...winesA, ...winesB]
  }

  const getWinesMerged = (winesA, winesB) => {
    let winesMerged = []
    let winesSmall = []
    let winesBig = getLongestCollection(winesA, winesB)

    const isWinesALengthEqualToWinesBLength = winesBig === -1
    const isWinesBLongest = winesBig === winesB

    if (isWinesALengthEqualToWinesBLength || isWinesBLongest) {
      winesBig = [...winesB]
      winesSmall = [...winesA]
    } else {
      winesSmall = [...winesB]
      winesBig = [...winesA]
    }

    for (let index = 0; index < winesSmall.length; index++) {
      const wineSmall = winesSmall[index]
      const wineAPositionInWinesB = getWinePositionInWines(
        wineSmall.node.frontmatter.nombre,
        winesBig,
      )

      if (wineAPositionInWinesB !== -1) {
        winesMerged.push(wineSmall)
      }
    }

    return [...winesMerged]
  }

  const getWinesFiltered = (newFiltersStatus) => {
    let mergedWinesFiltered = []

    for (let index = 0; index < newFiltersStatus.length; index++) {
      const mergeWinesFilteredIsEmpty = mergedWinesFiltered.length === 0
      const newFilterStatus = newFiltersStatus[index]
      const filteredWinesInCurrentFilterStatusIsEmpty =
        newFilterStatus.filtered.length === 0

      if (mergeWinesFilteredIsEmpty) {
        mergedWinesFiltered = [...newFilterStatus.filtered]
      } else if (!filteredWinesInCurrentFilterStatusIsEmpty) {
        mergedWinesFiltered = getWinesMerged(
          [...newFilterStatus.filtered],
          [...mergedWinesFiltered],
        )
      }
    }

    return mergedWinesFiltered
  }

  const getFilteredWinesByPrice = (type, options) => {
    let filtered = []

    for (let index = 0; index < options.length; index++) {
      const option = options[index]
      const { max, min } = getMaxAndMinByPriceOption(option)

      filtered = wines.filter(({ node }) => {
        const wine = node?.frontmatter
        const price = parsePriceToFloatNumber(wine[type])

        if (max && min) {
          return price > min && price < max
        } else if (max && !min) {
          return price < max
        } else if (!max && min) {
          return price > min
        } else {
          return false
        }
      })
    }

    return filtered
  }

  const getFilteredWinesByType = (type, options) => {
    let filtered = []

    for (let index = 0; index < options.length; index++) {
      const option = options[index]

      filtered = wines.filter(({ node }) => {
        const wine = node?.frontmatter
        return wine[type] === option.value
      })
    }

    return filtered
  }

  const getFilteredWines = (type, options) => {
    const isFilterSelectionEmpty = !options || options.length === 0
    const isFilterByPrice = type === filter.type.precio

    if (isFilterSelectionEmpty) {
      return wines
    }

    if (isFilterByPrice) {
      return getFilteredWinesByPrice(type, options)
    }

    return getFilteredWinesByType(type, options)
  }

  const getFiltersStatusUpdated = (type, options, filtered) => {
    const filtersStatusToUpdate = [...filtersStatus]

    filtersStatusToUpdate.forEach((filterStatus) => {
      if (filterStatus.type === type) {
        filterStatus.options = options
        const areWinesFiltered = filtered.length !== wines.length
        const anyWineFilteredPositionInWinesFilteredBefore = getWinePositionInWines(
          filtered[0].node.frontmatter.nombre,
          filterStatus.filtered,
        )
        const optionsIncreaseThanLastTime =
          anyWineFilteredPositionInWinesFilteredBefore === -1

        if (areWinesFiltered) {
          if (optionsIncreaseThanLastTime) {
            const joinedWinesFiltered = getWinesJoined(
              filtered,
              filterStatus.filtered,
            )
            filterStatus.filtered = joinedWinesFiltered
          } else {
            const mergedWinesFiltered = getWinesMerged(
              filtered,
              filterStatus.filtered,
            )
            filterStatus.filtered = mergedWinesFiltered
          }
        } else {
          filterStatus.filtered = filtered
        }
      }
    })
    return filtersStatusToUpdate
  }

  const handleChangeFilters = (type, options) => {
    const filteredWines = getFilteredWines(type, options)
    const newFiltersStatus = getFiltersStatusUpdated(
      type,
      options,
      filteredWines,
    )

    setFiltersStatus(newFiltersStatus)
    const newWinesFiltered = getWinesFiltered(newFiltersStatus)
    setWinesFiltered(newWinesFiltered)
  }

  return (
    <App navigation={navigation} seo={seo}>
      <div className={styles.children}>
        <div id={'top'}>
          <Header navigation={navigation} />
        </div>
        <div id={'body'} className={styles.body}>
          <header className={styles.header}>
            <Title2>{WINES_TITLE}</Title2>
          </header>
          <main className={styles.main}>
            <section className={styles.filters}>
              <Filters filters={filters} onChange={handleChangeFilters} />
            </section>
            <section className={styles.wines}>
              {winesFiltered.map(({ node }, i) => (
                <Wine key={`${node?.frontmatter?.nombre}-${i}`} wine={node} />
              ))}
            </section>
          </main>
        </div>
        <div id={'contact'} className={styles.contact}>
          <Contact image={contact.image} />
        </div>
      </div>
    </App>
  )
}

export default Wines
